/**
 * Tiny, safe algebraic expression parser/evaluator used to check student
 * answers such as "3(x+2)" or "6x + 4" by numeric equivalence.
 * Supports: numbers (dot or comma decimals), single-letter variables,
 * + − * / ^, parentheses, unary minus and implicit multiplication.
 */

type Tok = { t: 'num'; v: number } | { t: 'var'; v: string } | { t: 'op'; v: string } | { t: 'lp' } | { t: 'rp' };

export type Node =
  | { k: 'num'; v: number }
  | { k: 'var'; v: string }
  | { k: 'neg'; a: Node }
  | { k: 'bin'; op: '+' | '-' | '*' | '/' | '^'; a: Node; b: Node };

export function tokenizeExpr(src: string): Tok[] {
  const s = src
    .replace(/[−–]/g, '-')
    .replace(/[·×⋅]/g, '*')
    .replace(/÷/g, '/')
    .replace(/²/g, '^2')
    .replace(/³/g, '^3')
    .replace(/\s+/g, '');
  const out: Tok[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (/[0-9.,]/.test(c)) {
      let j = i;
      while (j < s.length && /[0-9.,]/.test(s[j])) j++;
      const raw = s.slice(i, j).replace(',', '.');
      const v = Number(raw);
      if (!Number.isFinite(v)) throw new Error('bad number');
      out.push({ t: 'num', v });
      i = j;
    } else if (/[a-zA-Z]/.test(c)) {
      out.push({ t: 'var', v: c.toLowerCase() });
      i++;
    } else if ('+-*/^'.includes(c)) {
      out.push({ t: 'op', v: c });
      i++;
    } else if (c === '(' || c === '[') {
      out.push({ t: 'lp' });
      i++;
    } else if (c === ')' || c === ']') {
      out.push({ t: 'rp' });
      i++;
    } else {
      throw new Error(`unexpected '${c}'`);
    }
  }
  // implicit multiplication
  const withImplicit: Tok[] = [];
  for (let k = 0; k < out.length; k++) {
    const a = out[k - 1];
    const b = out[k];
    if (a && (a.t === 'num' || a.t === 'var' || a.t === 'rp') && (b.t === 'num' || b.t === 'var' || b.t === 'lp')) {
      if (!(a.t === 'num' && b.t === 'num')) withImplicit.push({ t: 'op', v: '*' });
    }
    withImplicit.push(b);
  }
  return withImplicit;
}

export function parseExpr(src: string): Node {
  const toks = tokenizeExpr(src);
  let p = 0;
  const peek = () => toks[p];
  const eat = () => toks[p++];

  function primary(): Node {
    const t = eat();
    if (!t) throw new Error('unexpected end');
    if (t.t === 'num') return { k: 'num', v: t.v };
    if (t.t === 'var') return { k: 'var', v: t.v };
    if (t.t === 'lp') {
      const e = expression();
      const r = eat();
      if (!r || r.t !== 'rp') throw new Error('missing )');
      return e;
    }
    if (t.t === 'op' && t.v === '-') return { k: 'neg', a: unary() };
    if (t.t === 'op' && t.v === '+') return unary();
    throw new Error('unexpected token');
  }
  function unary(): Node {
    const t = peek();
    if (t && t.t === 'op' && (t.v === '-' || t.v === '+')) {
      eat();
      const a = unary();
      return t.v === '-' ? { k: 'neg', a } : a;
    }
    return power();
  }
  function power(): Node {
    const base = primary();
    const t = peek();
    if (t && t.t === 'op' && t.v === '^') {
      eat();
      return { k: 'bin', op: '^', a: base, b: unary() };
    }
    return base;
  }
  function term(): Node {
    let a = unary();
    for (;;) {
      const t = peek();
      if (t && t.t === 'op' && (t.v === '*' || t.v === '/')) {
        eat();
        a = { k: 'bin', op: t.v as '*' | '/', a, b: unary() };
      } else return a;
    }
  }
  function expression(): Node {
    let a = term();
    for (;;) {
      const t = peek();
      if (t && t.t === 'op' && (t.v === '+' || t.v === '-')) {
        eat();
        a = { k: 'bin', op: t.v as '+' | '-', a, b: term() };
      } else return a;
    }
  }
  const node = expression();
  if (p !== toks.length) throw new Error('trailing input');
  return node;
}

export function evalNode(n: Node, env: Record<string, number>): number {
  switch (n.k) {
    case 'num':
      return n.v;
    case 'var':
      if (!(n.v in env)) throw new Error(`unknown variable ${n.v}`);
      return env[n.v];
    case 'neg':
      return -evalNode(n.a, env);
    case 'bin': {
      const a = evalNode(n.a, env);
      const b = evalNode(n.b, env);
      switch (n.op) {
        case '+': return a + b;
        case '-': return a - b;
        case '*': return a * b;
        case '/': return a / b;
        case '^': return Math.pow(a, b);
      }
    }
  }
}

export function variablesOf(n: Node, acc = new Set<string>()): Set<string> {
  if (n.k === 'var') acc.add(n.v);
  else if (n.k === 'neg') variablesOf(n.a, acc);
  else if (n.k === 'bin') {
    variablesOf(n.a, acc);
    variablesOf(n.b, acc);
  }
  return acc;
}

const SAMPLE_POINTS = [-2.5, -1.25, 0.5, 1.75, 3.2, 4.6];

/** True when both expressions agree at several sample points. */
export function equivalent(a: string, b: string, vars: string[] = ['x']): boolean {
  try {
    const na = parseExpr(a);
    const nb = parseExpr(b);
    const allowed = new Set(vars);
    for (const v of variablesOf(na)) if (!allowed.has(v)) return false;
    for (let i = 0; i < SAMPLE_POINTS.length; i++) {
      const env: Record<string, number> = {};
      vars.forEach((v, j) => (env[v] = SAMPLE_POINTS[(i + j * 2) % SAMPLE_POINTS.length] + j * 0.37));
      const va = evalNode(na, env);
      const vb = evalNode(nb, env);
      if (!Number.isFinite(va) || !Number.isFinite(vb)) return false;
      if (Math.abs(va - vb) > 1e-6 * Math.max(1, Math.abs(vb))) return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Count top-level additive terms of an expression (for "simplified" checks). */
export function countTerms(src: string): number {
  try {
    const n = parseExpr(src);
    const walk = (x: Node): number => (x.k === 'bin' && (x.op === '+' || x.op === '-') ? walk(x.a) + walk(x.b) : 1);
    return walk(n);
  } catch {
    return Infinity;
  }
}

/** Parse a number, fraction ("3/4"), mixed number ("1 1/2"), percentage or "x = 4" style answer. */
export function parseNumberAnswer(raw: string): number | null {
  let s = raw.trim().replace(/[−–]/g, '-').replace(/\s+/g, ' ');
  s = s.replace(/^[a-zA-Z]\s*=\s*/, '');
  s = s.replace(/(kr|nok|cm²|cm2|m²|m2|cm³|cm3|m³|m3|cm|mm|km|kg|g|m|s|l|%|°|h|min|kr\.)$/i, '').trim();
  if (!s) return null;
  const mixed = s.match(/^(-?\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const w = Number(mixed[1]);
    const v = Number(mixed[2]) / Number(mixed[3]);
    return w < 0 ? w - v : w + v;
  }
  const f = s.match(/^(-?\d+(?:[.,]\d+)?)\s*\/\s*(-?\d+(?:[.,]\d+)?)$/);
  if (f) {
    const d = Number(f[2].replace(',', '.'));
    if (d === 0) return null;
    return Number(f[1].replace(',', '.')) / d;
  }
  const normalized = s.replace(/\s/g, '');
  // treat "1,5" as decimal comma, but "1,000" with exactly 3 digits after comma as thousands only when no other separators
  const n = Number(normalized.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}
